import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils";
import { Trash2, AlertTriangle } from "lucide-react";

// CS 21.86 (v8) -- shared delete-PIN UI. Mirrors the wholesaler pattern (CS 21.70)
// for franchises/distributors: HO sets a 4-digit PIN per entity (independent of the
// owner pricing PIN), public owner-unlocked links must enter it fresh per deletion.

export function deletePinErrorMessage(e, fallback = "Delete failed") {
  const d = e?.response?.data?.detail;
  if (d && typeof d === "object") {
    if (d.error === "delete_pin_locked") {
      const until = d.locked_until
        ? new Date(d.locked_until).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
        : "";
      return `Delete locked${until ? ` until ${until}` : ""} — too many wrong PIN attempts`;
    }
    if (d.error === "delete_pin_not_set") return "Delete PIN not set for this link — ask HO to set it first";
    if (d.error === "delete_pin_wrong") return `Incorrect delete PIN (${d.fails || "?"}/5 attempts)`;
  }
  return formatApiError(d) || fallback;
}

export function DeletePinControls({ entity, basePath, canEdit, onChanged, testidPrefix, sharedNote }) {
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState("");
  const fails = Number(entity.delete_pin_fails || 0);
  const lockedUntil = entity.delete_pin_locked_until ? new Date(entity.delete_pin_locked_until) : null;
  const locked = !!(lockedUntil && lockedUntil > new Date());
  const pillCls = !entity.delete_pin_set
    ? "text-slate-500 bg-slate-50 border-slate-200"
    : locked
      ? "text-red-800 bg-red-50 border-red-200"
      : fails > 0
        ? "text-amber-800 bg-amber-50 border-amber-200"
        : "text-emerald-800 bg-emerald-50 border-emerald-200";

  const save = async () => {
    if (!/^\d{4}$/.test(pin)) return toast.error("Delete PIN must be exactly 4 digits");
    try {
      await api.post(`${basePath}/delete-pin`, { pin });
      toast.success("Delete PIN set — the public link can now delete pieces with this PIN");
      setOpen(false);
      setPin("");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const unset = async () => {
    if (!window.confirm("Remove the delete PIN? Deletion from the public link becomes unavailable.")) return;
    try {
      await api.delete(`${basePath}/delete-pin`);
      toast.success("Delete PIN removed");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const resetLockout = async () => {
    try {
      await api.post(`${basePath}/delete-pin/reset-lockout`);
      toast.success("Lockout cleared");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="flex items-center flex-wrap gap-2" data-testid={`${testidPrefix}-delete-pin-controls`}>
      <span className={`text-[11px] px-2 py-0.5 rounded-full border ${pillCls}`} data-testid={`${testidPrefix}-delete-pin-status`}>
        Delete PIN:{" "}
        {!entity.delete_pin_set
          ? "Not set"
          : locked
            ? `Locked until ${lockedUntil.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
            : fails > 0
              ? `Active · ${fails}/5 wrong tries`
              : "Active"}
      </span>
      {canEdit && (
        <button
          onClick={() => { setOpen(true); setPin(""); }}
          data-testid={`${testidPrefix}-set-delete-pin`}
          className="text-[11px] px-2 py-0.5 rounded-md border border-red-200 text-red-700 hover:bg-red-50 flex items-center gap-1"
          title="4-digit PIN gating product deletion on the public link (separate from the owner pricing PIN)"
        >
          <Trash2 className="w-3 h-3" /> {entity.delete_pin_set ? "Change Delete PIN" : "Set Delete PIN"}
        </button>
      )}
      {canEdit && entity.delete_pin_set && locked && (
        <button
          onClick={resetLockout}
          data-testid={`${testidPrefix}-delete-pin-reset-lockout`}
          className="text-[11px] px-2 py-0.5 rounded-md border border-[#0D1B2A]/15 hover:bg-[#FAF8F3]"
        >
          Reset lockout
        </button>
      )}
      {canEdit && entity.delete_pin_set && (
        <button
          onClick={unset}
          data-testid={`${testidPrefix}-delete-pin-unset`}
          className="text-[11px] px-2 py-0.5 rounded-md border border-[#0D1B2A]/15 hover:bg-[#FAF8F3]"
        >
          Remove
        </button>
      )}

      <Dialog open={open} onOpenChange={(o) => { if (!o) { setOpen(false); setPin(""); } }}>
        <DialogContent className="bg-white" data-testid={`${testidPrefix}-delete-pin-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {entity.delete_pin_set ? "Change Delete PIN" : "Set Delete PIN"}
            </DialogTitle>
          </DialogHeader>
          <DialogDescription asChild>
            <p className="text-sm text-slate-500">
              Exactly 4 digits, separate from the owner pricing PIN. The link owner must enter it
              to delete a piece from the public link. 5 wrong tries → 10-minute lockout.
              {sharedNote ? ` ${sharedNote}` : ""}
            </p>
          </DialogDescription>
          <Input
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
            maxLength={4}
            inputMode="numeric"
            placeholder="0000"
            className="text-2xl tracking-[0.5em] text-center h-14"
            data-testid={`${testidPrefix}-delete-pin-input`}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); setPin(""); }}>Cancel</Button>
            <Button
              onClick={save}
              disabled={!/^\d{4}$/.test(pin)}
              className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-40"
              data-testid={`${testidPrefix}-delete-pin-save`}
            >
              Save Delete PIN
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// CS 21.86 -- public-link delete confirmation. A fresh 4-digit delete PIN is required
// every time the dialog opens; the PIN is cleared on close/success.
export function PublicDeleteDialog({ open, onOpenChange, codes, busy, onConfirm, testidPrefix, scopeNote }) {
  const [pin, setPin] = useState("");
  useEffect(() => { if (open) setPin(""); }, [open]);
  const count = codes.length;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) { setPin(""); onOpenChange(false); } }}>
      <DialogContent className="bg-white max-w-md" data-testid={`${testidPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-6 h-6" /> Delete {count} piece{count === 1 ? "" : "s"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <DialogDescription asChild>
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid={`${testidPrefix}-warning`}>
              {scopeNote}
            </div>
          </DialogDescription>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Jewel codes</div>
            <div
              className="max-h-32 overflow-y-auto rounded-md border border-[#0D1B2A]/10 bg-slate-50 p-2 font-mono text-xs space-y-0.5"
              data-testid={`${testidPrefix}-codes`}
            >
              {codes.map((c) => <div key={c}>{c}</div>)}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Delete PIN (4 digits)</div>
            <Input
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
              maxLength={4}
              inputMode="numeric"
              placeholder="0000"
              autoFocus
              className="text-2xl tracking-[0.5em] text-center h-14"
              data-testid={`${testidPrefix}-pin-input`}
            />
            <p className="text-[11px] text-slate-400 mt-1">Required for every deletion. 5 wrong tries → 10-minute lockout.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { setPin(""); onOpenChange(false); }} disabled={busy} data-testid={`${testidPrefix}-cancel`}>
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(pin)}
            disabled={busy || !/^\d{4}$/.test(pin)}
            className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-40"
            data-testid={`${testidPrefix}-confirm`}
          >
            {busy ? "Deleting…" : `Delete ${count} piece${count === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
